import React from 'react';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';
import {
	SelectedReleaseInfo
} from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext.types';
import { useFacilitySelection } from '@components/SchedulePage/utilities/useFacilitySelection/useFacilitySelection';
import { ScheduleColumnData } from '@data/schedules/types/ScheduleColumnData';
import { useRouteParams } from '@components/BlockRelease/utils/hooks/useRouteParams/useRouteParams';
import { DEFAULT_SELECTION_ALL } from '@utilities/constants';

interface UseGetSelectedReleaseInfoProps {
	releases: BlockReleaseApiResponse[];
	schedules: ScheduleColumnData[];
	selectedSurgeonFilter: string;
	prevSelectedRelease: React.MutableRefObject<BlockReleaseApiResponse | undefined>;
}

interface UseGetSelectedReleaseInfoReturn {
	selectedReleaseInfo: SelectedReleaseInfo;
}

/**
 * @param releases - List of releases
 * @param schedules - Schedule Info for the selected release
 * @param selectedSurgeonFilter - Selected surgeon filter
 * @param prevSelectedRelease - Previous selected release
 * This hooks takes in the above params and returns the selected release and its info
 * This hook holds the logic for selecting the block card when the user navigates to the block release page
 */

export const useGetSelectedReleaseInfo = ({
	releases,
	schedules,
	prevSelectedRelease,
	selectedSurgeonFilter,
}: UseGetSelectedReleaseInfoProps): UseGetSelectedReleaseInfoReturn => {
	const { getFacilityByUnit } = useFacilitySelection();
	const { surgeonNpi, recommendationId, hasAllParams } = useRouteParams();

	const selectedRelease = React.useMemo(() => {
		let selected;

		const matchedPrevRelease = releases.find((rel) => {
			return rel.id === prevSelectedRelease.current?.id;
		});

		const isPrevSelectedReleasePending = matchedPrevRelease?.state === BlockReleaseState.PENDING_RELEASE ||
			(matchedPrevRelease?.state === BlockReleaseState.FLIP_RECOMMENDATION &&
			// check state of originalReleases when top level state = FLIP_RECOMMENDATION since PENDING_RELEASE state is nested
			matchedPrevRelease?.originalReleases?.every((release) => release.state === BlockReleaseState.PENDING_RELEASE));

		const isPrevSelectedReleaseNotFound = !matchedPrevRelease;

		if (selectedSurgeonFilter === DEFAULT_SELECTION_ALL && !(isPrevSelectedReleasePending || isPrevSelectedReleaseNotFound)) {
			selected = prevSelectedRelease.current;
		}

		if (
			selectedSurgeonFilter !== DEFAULT_SELECTION_ALL &&
			prevSelectedRelease.current?.npis.includes(selectedSurgeonFilter) &&
			!(isPrevSelectedReleasePending || isPrevSelectedReleaseNotFound) &&
			!hasAllParams
		) {
			selected = prevSelectedRelease.current;
		}

		if (recommendationId) {
			const match = releases.find(release => release.id === recommendationId);
			selected = match ?? releases.find(release => release.state !== BlockReleaseState.PENDING_RELEASE);
		}

		if (!selected && hasAllParams) {
			selected = releases.find(release => release.state === BlockReleaseState.NOT_RELEASED);
		}

		if (!selected) {
			selected = releases.find(release =>
				(
					release.state === BlockReleaseState.FLIP_RECOMMENDATION && release.originalReleases
						? release.originalReleases.every(
							originalRelease =>
								originalRelease.state === BlockReleaseState.RECOMMENDED_RELEASE
						)
						: release.state === BlockReleaseState.RECOMMENDED_RELEASE ||
						release.state === BlockReleaseState.NOT_RELEASED
				) &&
				(surgeonNpi === 'ALL' || release.npis.includes(surgeonNpi))
			);
		}

		return selected;
	}, [hasAllParams, prevSelectedRelease, recommendationId, releases, selectedSurgeonFilter, surgeonNpi]);


	const selectedReleaseInfo: SelectedReleaseInfo = React.useMemo(() => {
		if (!selectedRelease) {
			return {} as SelectedReleaseInfo;
		}

		const scheduleInfo = schedules.length ? schedules[0] : null;

		return {
			selectedRelease,
			blockInfo: scheduleInfo,
			facility: getFacilityByUnit(selectedRelease.unit),
		};
	}, [selectedRelease, schedules, getFacilityByUnit]);

	return { selectedReleaseInfo };
};
